import { useEffect, useRef, useState } from "react";

import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { buscarCep, mascaraDoCep } from "../../helpers/entrega";
import { aplicarViaCep } from "../../helpers/viaCep";
import { cn } from "../../lib/utils";

// Endereço de entrega no formato do backend ({cep, endereco, numero, ...}).
// Usado na venda, na ficha do contato e no checkout da loja.
//
// `onCampo(campo, valor)` campo a campo, e não o objeto inteiro: o CEP
// preenche o resto depois de uma ida ao ViaCEP, e quem digitou no número
// nesse meio-tempo não pode perder o que digitou.
//
// Começa só pelo CEP: o resto aparece com o CEP completo (o ViaCEP já traz
// rua, bairro, cidade e UF), com algum campo já preenchido, ou em "Não sei
// o CEP". `inputClassName`: a loja usa campo mais alto, para o dedo.
const OPCIONAIS = ["complemento", "referencia"];
const CAMPOS = [
	{ campo: "cep", rotulo: "CEP", classe: "@md:col-span-2", extra: { inputMode: "numeric", autoComplete: "postal-code", placeholder: "00000-000" } },
	{ campo: "endereco", rotulo: "Rua", classe: "@md:col-span-4", extra: { autoComplete: "address-line1" } },
	{ campo: "numero", rotulo: "Número", classe: "@md:col-span-2" },
	{ campo: "complemento", rotulo: "Complemento", classe: "@md:col-span-4", extra: { placeholder: "Apto, bloco…", autoComplete: "address-line2" } },
	{ campo: "bairro", rotulo: "Bairro", classe: "@md:col-span-2" },
	{ campo: "cidade", rotulo: "Cidade", classe: "@md:col-span-3", extra: { autoComplete: "address-level2" } },
	{ campo: "uf", rotulo: "UF", classe: "@md:col-span-1", extra: { maxLength: 2, autoComplete: "address-level1" } },
	{ campo: "referencia", rotulo: "Referência", classe: "@md:col-span-6", extra: { placeholder: "Perto de…, portão azul…" } },
];

const CamposDoEndereco = ({
	idBase = "endereco",
	valor,
	onCampo,
	disabled,
	className,
	inputClassName = "h-8",
}) => {
	const atual = valor || {};
	// Corrida do ViaCEP: cada CEP novo vira uma versão, e só a resposta da
	// versão atual entra — e só no que a pessoa não mexeu desde que a busca
	// começou (`aplicarViaCep`). `valorAgora` é o endereço da tela quando a
	// resposta chega, não o de quando a busca saiu.
	const ultimoCep = useRef(null);
	const versao = useRef(0);
	const editados = useRef(new Set());
	const valorAgora = useRef(atual);
	useEffect(() => {
		valorAgora.current = valor || {};
	});
	const [semCep, setSemCep] = useState(false);
	const completo =
		semCep ||
		String(atual.cep || "").replace(/\D/g, "").length === 8 ||
		CAMPOS.some(({ campo }) => campo !== "cep" && String(atual[campo] || "").trim());

	const mudarCep = async (texto) => {
		const cep = mascaraDoCep(texto);
		onCampo("cep", cep);
		const digitos = cep.replace(/\D/g, "");
		// Mesmos dígitos (só a máscara mudou): a busca em andamento continua valendo.
		if (digitos === ultimoCep.current) return;
		ultimoCep.current = digitos;
		const minha = ++versao.current;
		editados.current = new Set();
		if (digitos.length !== 8) return;
		const achado = await buscarCep(digitos);
		if (minha !== versao.current) return;
		Object.entries(
			aplicarViaCep(valorAgora.current, digitos, achado, [...editados.current]),
		).forEach(([campo, v]) => onCampo(campo, v));
	};

	const mudarCampo = (campo, texto) => {
		editados.current.add(campo);
		onCampo(campo, campo === "uf" ? texto.toUpperCase() : texto);
	};

	return (
		<div className="@container">
			<div className={cn("grid grid-cols-2 gap-3 @md:grid-cols-6", className)}>
				{(completo ? CAMPOS : CAMPOS.slice(0, 1)).map(({ campo, rotulo, classe, extra }) => (
					<div
						key={campo}
						className={cn(
							"space-y-1",
							// Estreito (2 colunas): CEP, rua, bairro e referência na linha
							// inteira; número/complemento e cidade/UF em pares. Sem buraco.
							["cep", "endereco", "bairro", "referencia"].includes(campo) && "col-span-2",
							classe,
						)}
					>
						<Label htmlFor={`${idBase}-${campo}`} className="text-xs">
							{rotulo}
							{OPCIONAIS.includes(campo) && (
								<span className="font-normal text-muted-foreground"> (opcional)</span>
							)}
						</Label>
						<Input
							id={`${idBase}-${campo}`}
							className={inputClassName}
							value={campo === "cep" ? mascaraDoCep(atual.cep || "") : atual[campo] || ""}
							disabled={disabled}
							onChange={(e) =>
								campo === "cep" ? mudarCep(e.target.value) : mudarCampo(campo, e.target.value)
							}
							{...extra}
						/>
					</div>
				))}
			</div>
			{!completo && !disabled && (
				<button
					type="button"
					onClick={() => setSemCep(true)}
					className="-ml-2 mt-0.5 inline-flex min-h-11 items-center rounded-md px-2 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					Não sei o CEP
				</button>
			)}
		</div>
	);
};

export default CamposDoEndereco;
